import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { TranslationService } from '../../services/translation.service';
import { TranslationErrorHandlerService } from '../../services/translation-error-handler.service';

@Component({
  selector: 'app-translation-status',
  standalone: true,
  imports: [],
  templateUrl: `./translation-status.component.html`,
  styleUrl: './translation-status.component.scss',
})
export class TranslationStatusComponent implements OnInit, OnDestroy {
  isLoading = false;
  hasError = false;
  errorMessage = '';
  successMessage = '';
  showSuccess = false;
  currentLanguage = '';
  hasCriticalErrors = false;
  errorStats: any = null;
  showDebugInfo = false; // Set to true for development
  showStatus = true; // Can be controlled by user preference

  private destroy$ = new Subject<void>();

  constructor(
    private translationService: TranslationService,
    private errorHandler: TranslationErrorHandlerService,
  ) {}

  ngOnInit(): void {
    // Subscribe to loading state
    this.translationService.translationLoading$.pipe(takeUntil(this.destroy$)).subscribe((isLoading) => {
      this.isLoading = isLoading;
      if (isLoading) {
        this.showSuccess = false;
      }
    });

    // Subscribe to errors
    this.translationService.translationError$.pipe(takeUntil(this.destroy$)).subscribe((error) => {
      this.hasError = !!error;
      this.errorMessage = error || '';
      if (error) {
        this.showSuccess = false;
      }
    });

    // Subscribe to language changes for success feedback
    this.translationService.currentLanguage$.pipe(takeUntil(this.destroy$)).subscribe((language) => {
      if (language !== this.currentLanguage && this.currentLanguage && !this.isLoading && !this.hasError) {
        this.showSuccessMessage(`Language changed to ${language.toUpperCase()}`);
      }
      this.currentLanguage = language;
    });

    // Update error statistics periodically
    this.updateErrorStats();
    setInterval(() => this.updateErrorStats(), 5000);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private showSuccessMessage(message: string): void {
    this.successMessage = message;
    this.showSuccess = true;

    // Auto-hide after 3 seconds
    setTimeout(() => {
      this.showSuccess = false;
    }, 3000);
  }

  private updateErrorStats(): void {
    this.hasCriticalErrors = this.translationService.hasCriticalErrors();
    this.errorStats = this.translationService.getErrorStats();
  }

  attemptRecovery(): void {
    this.translationService.attemptRecovery().subscribe({
      next: (success) => {
        if (success) {
          this.showSuccessMessage('Translation recovery successful');
        } else {
          this.errorMessage = 'Recovery failed. Using fallback language.';
        }
      },
      error: () => {
        this.errorMessage = 'Recovery failed. Please refresh the page.';
      },
    });
  }
}
